import React, { FC } from 'react';
import styled from 'styled-components';

interface IStepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  stepLabels: string[];
  onStepClick?: (step: number) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-right: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};
  width: 175px;
`;

const StepItem = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  display: flex;
  gap: 12px;
  align-items: center;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surface.onSurfaceHover};
  }

  ${({ isActive }) =>
    isActive &&
    `
    background-color: ${({ theme }: any) => theme.colors.primary.primaryContainer};
  `}
`;

const StepNumber = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 600;
  border: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};
  background-color: ${({ theme, isCompleted, isActive }) => {
    if (isCompleted) {
      return theme.colors.success.success;
    }
    if (isActive) {
      return theme.colors.primary.primary;
    }
    return theme.colors.surface.surface;
  }};
  color: ${({ theme, isCompleted, isActive }) => {
    if (isCompleted) {
      return theme.colors.success.onSuccess;
    }
    if (isActive) {
      return theme.colors.primary.onPrimary;
    }
    return theme.colors.surface.onSurface;
  }};
`;

const StepLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.surface.onSurface};
`;

const Connector = styled.div`
  width: 2px;
  height: 12px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceOutline};
  margin: 2px 0 2px 11px;
`;

const StepIndicator: FC<IStepIndicatorProps> = ({
  currentStep,
  totalSteps,
  stepLabels,
  onStepClick,
}) => {
  return (
    <Container>
      {stepLabels.map((label, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isCompleted = stepNumber < currentStep;

        return (
          <div key={stepNumber}>
            <StepItem
              isActive={isActive}
              isCompleted={isCompleted}
              onClick={() => onStepClick?.(stepNumber)}
            >
              <StepNumber isActive={isActive} isCompleted={isCompleted}>
                {isCompleted ? '✓' : stepNumber}
              </StepNumber>
              <StepLabel>{label}</StepLabel>
            </StepItem>
            {stepNumber < totalSteps && <Connector />}
          </div>
        );
      })}
    </Container>
  );
};

export default StepIndicator;
